import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import classNames from 'classnames';
import ResumeViewer from '../../Components/ResumeView/ResumeViewer';
import ResumeDownloadButton from '../../Components/ResumeView/ResumeDownloadButton';
import { getApiStatus } from '../../Reducers/ApiStatusReducer';
import { getActiveTab, getActiveSourceName } from '../../Reducers/JobCandidatesTabReducer';
import styles from './ResumeViewContainer.module.scss';
import {
  isResumeUnlockable,
  isSocialResumeGenerationAllowed,
  isSocialResumeGenerationEnabled,
} from '../../Utils/ResumeUtils';

const mapStateToProps = state => ({
  resumeDownloadApiStatus: getApiStatus(state, 'resumeDownloadStatus'),
  activeTab: getActiveTab(state),
  activeSourceName: getActiveSourceName(state),
});

function ResumeViewContainer(props) {
  const {
    candidate = {},
    isCandidateViewHeaderVisible,
    setCandidateViewHeaderVisibility,
    keywordsToHighlight,
    mustHavesKeywords,
    candidateListStatus,
    candidateDetailsStatus,
    isTryNow,
    downloadResume,
    allowResumeDownload = true,
    version,
    jobGuid,
    currentJobDetails,
    resumeDownloadApiStatus,
    isJobActionsAllowed,
    jobId,
    unlockCandidateResume,
    resumeUnlockStatus,
    featureToggleList,
    connectInfo,
    selectPlan,
    activeTab,
    activeSourceName,
    isUserProhibitedFromPerformingActions,
  } = props;
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onScroll = () => {
      if (ref.current.scrollTop === 0) setCandidateViewHeaderVisibility(true);
      else setCandidateViewHeaderVisibility(false);
    };
    ref.current.addEventListener('scroll', onScroll);
    return () => {
      ref.current.removeEventListener('scroll', onScroll);
    };
  }, []);

  const isSocialResumeDownloadAllowed = isSocialResumeGenerationAllowed(candidate, featureToggleList, connectInfo);
  const isSocialResumeDownloadEnabled = isSocialResumeGenerationEnabled(candidate, featureToggleList);

  const resumes = candidate.Resumes;
  let resume;
  if (resumes && resumes.length > 0 && resumes[0].Content) {
    [resume] = resumes;
  }

  const checkIfResumeIconViewRequired = (resume, isSocialResumeDownloadAllowed, isSocialResumeDownloadEnabled) => {
    const supportedMediaTypes = ['application/pdf', 'text/html', 'text/plain'];
    const isUnsupportedMediaType = resume && !supportedMediaTypes.includes(resume.MediaType);
    return isSocialResumeDownloadAllowed || isSocialResumeDownloadEnabled || !resume || isUnsupportedMediaType;
  };

  const isResumeIconViewRequired = checkIfResumeIconViewRequired(
    resume,
    isSocialResumeDownloadAllowed,
    isSocialResumeDownloadEnabled
  );

  const resumeDownloadButton = (
    <ResumeDownloadButton
      resume={resume}
      isDisable={isTryNow}
      downloadResume={downloadResume}
      candidate={candidate}
      portal={candidate.Portal}
      version={version}
      jobGuid={jobGuid}
      currentJobDetails={currentJobDetails}
      resumeDownloadApiStatus={resumeDownloadApiStatus}
      resumes={candidate.Resumes}
      isUnlockable={isJobActionsAllowed && isResumeUnlockable(candidate)}
      isUnlocked={candidate.IsUnlocked}
      jobId={jobId}
      candidateDetails={candidate}
      unlockCandidateResume={unlockCandidateResume}
      resumeUnlockStatus={resumeUnlockStatus}
      resumeDownloadStatus={resumeDownloadApiStatus}
      allowResumeDownload={allowResumeDownload !== undefined ? allowResumeDownload : true}
      highlights={_.uniq([...(keywordsToHighlight || []), ...(candidate.Keywords || [])])}
      isSocialCandidate={isSocialResumeDownloadAllowed}
      isTryNow={isTryNow}
      selectPlan={selectPlan}
      featureToggleList={featureToggleList}
    />
  );

  return (
    <div
      className={classNames(styles.resumeViewerContainer, { [styles.extendView]: !isCandidateViewHeaderVisible })}
      ref={ref}
    >
      <div>
        <div
          className={classNames(styles.resumeViewer, {
            [styles.resumeIconView]: isResumeIconViewRequired,
          })}
        >
          <ResumeViewer
            highlights={_.uniq([
              ...(keywordsToHighlight || []),
              ...(activeTab === 'sourced' && activeSourceName !== 'AryaRecommended' ? [] : candidate.Keywords || []),
            ])}
            mustHavesKeywords={mustHavesKeywords}
            resume={resume}
            candidate={candidate}
            candidateDetailsStatus={candidateDetailsStatus}
            candidateListStatus={candidateListStatus}
            isSocialResumeDownloadAllowed={isSocialResumeDownloadAllowed}
            isSocialResumeGenerationEnabled={isSocialResumeDownloadEnabled}
            portal={candidate.Portal}
            version={version}
            isDisable={isTryNow}
            downloadResume={downloadResume}
            jobGuid={jobGuid}
            currentJobDetails={currentJobDetails}
            resumeDownloadApiStatus={resumeDownloadApiStatus}
            resumes={candidate.Resumes}
            isUnlockable={isJobActionsAllowed && isResumeUnlockable(candidate)}
            isUnlocked={candidate.IsUnlocked}
            jobId={jobId}
            candidateDetails={candidate}
            unlockCandidateResume={unlockCandidateResume}
            resumeUnlockStatus={resumeUnlockStatus}
            resumeDownloadStatus={resumeDownloadApiStatus}
            allowResumeDownload={allowResumeDownload !== undefined ? allowResumeDownload : true}
            isSocialCandidate={isSocialResumeDownloadAllowed}
            isTryNow={isTryNow}
            selectPlan={selectPlan}
            featureToggleList={featureToggleList}
            resumeDownloadButton={resumeDownloadButton}
            isUserProhibitedFromPerformingActions={isUserProhibitedFromPerformingActions}
          />
        </div>
        {((allowResumeDownload && resume && !isUserProhibitedFromPerformingActions) ||
          isSocialResumeDownloadAllowed) && <div className={styles.resumeDownload}>{resumeDownloadButton}</div>}
      </div>
    </div>
  );
}

export default connect(mapStateToProps)(ResumeViewContainer);
export { ResumeViewContainer as ResumeViewContainerWithoutStore };
